import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { SelectSearchState } from './select-search-state';

// A search box for the top of a mat-select's list. Place it inside the select, above the options, and
// list the options from the state it is given:
//
//   <mat-select>
//     <app-select-search [search]="search"></app-select-search>
//     <mat-option *ngFor="let item of search.options" [value]="item">{{ item }}</mat-option>
//   </mat-select>
@Component({
  selector: 'app-select-search',
  templateUrl: './select-search.component.html',
  styleUrls: ['./select-search.component.scss']
})
export class SelectSearchComponent implements OnInit, OnDestroy {
  @Input() search: SelectSearchState<any>;

  @ViewChild('bar', { static: true }) bar: ElementRef<HTMLElement>;
  @ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;

  private openedSubscription: Subscription;
  private keySubscription: Subscription;

  constructor(@Inject(MatSelect) private select: MatSelect) { }

  ngOnInit(): void {
    this.openedSubscription = this.select.openedChange.subscribe(opened => this.onPanelToggle(opened));
  }

  ngOnDestroy(): void {
    this.openedSubscription?.unsubscribe();
    this.keySubscription?.unsubscribe();
  }

  onInput(term: string) {
    this.search.setTerm(term);

    // Enter takes the highlighted option, so highlight the first match once the list has re-rendered.
    setTimeout(() => this.highlightFirstMatch());
  }

  clear() {
    this.search.reset();
    this.input.nativeElement.focus();
  }

  onKeydown(event: KeyboardEvent) {
    // Typing in the box must not reach the select: it would treat letters as type-ahead, toggle the
    // highlighted option on space and jump to the first/last option on Home/End. Arrows, Enter,
    // Escape and Tab still pass through so the list stays keyboard driven.
    if (event.key.length === 1 || event.key === 'Home' || event.key === 'End') {
      event.stopPropagation();
    }
  }

  private onPanelToggle(opened: boolean) {
    this.keySubscription?.unsubscribe();

    if (!opened) {
      this.search.reset();
      return;
    }

    // mat-select scrolls as if its options started at the top of the panel, so with the search bar
    // above them, keyboard navigation can leave the active option below the fold.
    this.keySubscription = this.select._keyManager.change.subscribe(() => this.revealActiveOption());

    // The panel has just rendered, so move focus into the search box.
    setTimeout(() => {
      this.input.nativeElement.focus();
      this.revealActiveOption();
    });
  }

  private highlightFirstMatch() {
    const firstMatch = this.search.firstMatchIndex;

    if (this.select.panelOpen && firstMatch < this.select.options.length) {
      this.select._keyManager.setActiveItem(firstMatch);
    }
  }

  private revealActiveOption() {
    const panel: HTMLElement = this.select.panel?.nativeElement;
    const option = this.select._keyManager?.activeItem?._getHostElement();

    if (!panel || !option) {
      return;
    }

    const panelRect = panel.getBoundingClientRect();
    const optionRect = option.getBoundingClientRect();
    const visibleTop = panelRect.top + this.bar.nativeElement.offsetHeight;

    if (optionRect.top < visibleTop) {
      panel.scrollTop -= visibleTop - optionRect.top;
    } else if (optionRect.bottom > panelRect.bottom) {
      panel.scrollTop += optionRect.bottom - panelRect.bottom;
    }
  }
}
